<template>
  <div>
    <div class="form-group">
      <label for="discountName">{{ $t('shop.discounts.name') }}</label>
      <input
        id="discountName"
        v-model="form.name"
        class="form-control"
        :class="{ 'is-invalid': errors.name }"
        :placeholder="$t('shop.discounts.namePlaceholder')"
        required
      >
      <div
        v-if="errors.name"
        class="invalid-feedback"
      >
        {{ errors.name }}
      </div>
    </div>

    <div class="form-group">
      <label for="discountCondition">{{ $t('shop.discounts.condition') }}</label>
      <textarea
        id="discountCondition"
        v-model="form.condition"
        class="form-control money-font"
        :class="{ 'is-invalid': errors.condition }"
        :placeholder="$t('shop.discounts.conditionPlaceholder')"
        rows="4"
      />
      <small class="form-text text-muted">
        {{ $t('shop.discounts.conditionHelp') }}
      </small>
      <div
        v-if="errors.condition"
        class="invalid-feedback"
      >
        {{ errors.condition }}
      </div>
    </div>

    <div class="form-group">
      <label for="discountPayerCondition">{{ $t('shop.discounts.payerCondition') }}</label>
      <textarea
        id="discountPayerCondition"
        v-model="form.payerCondition"
        class="form-control money-font"
        :class="{ 'is-invalid': errors.payerCondition }"
        :placeholder="$t('shop.discounts.payerConditionPlaceholder')"
        rows="4"
      />
      <small class="form-text text-muted">
        {{ $t('shop.discounts.payerConditionHelp') }}
      </small>
      <div
        v-if="errors.payerCondition"
        class="invalid-feedback"
      >
        {{ errors.payerCondition }}
      </div>
    </div>

    <div class="form-group">
      <div class="form-check">
        <input
          id="discountApplyToPast"
          v-model="form.applyToPast"
          class="form-check-input"
          type="checkbox"
        >
        <label
          class="form-check-label"
          for="discountApplyToPast"
        >
          {{ $t('shop.discounts.applyToPast') }}
        </label>
      </div>
      <small class="form-text text-muted">
        {{ $t('shop.discounts.applyToPastHelp') }}
      </small>
    </div>

    <div class="form-group">
      <label for="discountPriceModifier">{{ $t('shop.discounts.priceModifier') }}</label>
      <textarea
        id="discountPriceModifier"
        v-model="form.priceModifier"
        class="form-control money-font"
        :class="{ 'is-invalid': errors.priceModifier }"
        :placeholder="$t('shop.discounts.priceModifierPlaceholder')"
        rows="4"
      />
      <small class="form-text text-muted">
        {{ $t('shop.discounts.priceModifierHelp') }}
      </small>
      <div
        v-if="errors.priceModifier"
        class="invalid-feedback"
      >
        {{ errors.priceModifier }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  props: {
    discount: {
      type: Object,
      required: true,
    },
    catalogId: {
      type: [String, Number],
      required: true,
    },
    pending: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        name: '',
        condition: '',
        payerCondition: '',
        applyToPast: false,
        priceModifier: '',
      },
      errors: {},
    };
  },
  watch: {
    discount: {
      handler(newDiscount) {
        if (newDiscount) {
          this.form = {
            name: newDiscount.name || '',
            condition: newDiscount.condition || '',
            payerCondition: newDiscount.payerCondition || '',
            applyToPast: newDiscount.applyToPast || false,
            priceModifier: newDiscount.priceModifier || '',
          };
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('shop', [
      'createDiscount',
      'updateDiscount',
      'deleteDiscount']),
    async submit() {
      this.errors = {};

      if (!this.validateForm()) {
        return;
      }

      try {
        if (this.discount.id) {
          await this.updateDiscount({
            params: {
              catalogId: this.catalogId,
              discountId: this.discount.id,
            },
            data: this.form,
          });
        } else {
          await this.createDiscount({
            params: {
              catalogId: this.catalogId,
            },
            data: this.form,
          });
        }

        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        this.handleError(error);
      }
    },
    async remove() {
      if (!this.discount.id) {
        return;
      }

      try {
        await this.deleteDiscount({
          params: {
            catalogId: this.catalogId,
            discountId: this.discount.id,
          },
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        this.handleError(error);
      }
    },
    validateForm() {
      this.errors = {};
      let isValid = true;

      if (!this.form.name.trim()) {
        this.errors.name = this.$t('shop.discounts.errors.nameRequired');
        isValid = false;
      }

      if (!this.form.condition.trim()) {
        this.errors.condition = this.$t('shop.discounts.errors.conditionRequired');
        isValid = false;
      }

      if (!this.form.payerCondition.trim()) {
        this.errors.payerCondition = this.$t('shop.discounts.errors.payerConditionRequired');
        isValid = false;
      }

      if (!this.form.priceModifier.trim()) {
        this.errors.priceModifier = this.$t('shop.discounts.errors.priceModifierRequired');
        isValid = false;
      }

      return isValid;
    },
    handleError(error) {
      if (error.response && error.response.data && error.response.data.errors) {
        this.errors = error.response.data.errors;
      } else {
        this.$store.dispatch('notify/error', {
          message: error.response?.data?.message || this.$t('shop.discounts.errors.generic'),
        });
      }
    },
  },
};
</script>
